import useUserDetailScope from '../../hooks/useUserDetailScope';
import PageTabs from '../../ui/PageTabs';
import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '../../utils/i18n';
import { displayUserEmail, formatDateTime } from '../../utils/format';
import { formatDateTimeKST } from '../../utils/kstDateTime';
import UserAvatar from '../UserAvatar';
import PlatformBadge, { normalizePlatform } from '../PlatformBadge/PlatformBadge';
import { useNavigate } from 'react-router-dom';
import { REPORT_REASONS } from '../../utils/reportReasons';
import UserUGCDetail from '../../page/Moderation/UserUGCDetail';
import UserActivityPanels from './UserActivityPanels';
import UserDmHistoryPanel from './UserDmHistoryPanel';
import ProfileTagDetail from './ProfileTagDetail';
import { useApiEnvironment } from '../../context/ApiEnvironmentContext';
import { API_ENV_TEST } from '../../utils/apiEnvironment';
import { EDITABLE_ACCOUNT_TYPES, isEditableAccountType } from '../../utils/accountType';
import {
  getUserById,
  getUserBlocks,
  getUserDevices,
  getUserLikes,
  getUserLikesReceived,
  getUserAccount,
  getUserNotifications,
  patchUserAccountSettings,
  patchUserAccountType,
} from '../../api/authAdmin';
import { getUserAccountInactiveReason } from '../../utils/userBanActions';
import { canManageSuspension } from '../../utils/userSuspensionActions';
import UserBanActionButton from '../UserBanActionButton/UserBanActionButton';


const PAGE_SIZE = 10;

function getAccountStatusLabel(status) {
  return status === 1 ? t('user.accountStatus.active') : t('user.accountStatus.inactive');
}

function readLocationFlag(value) {
  if (value === 1 || value === '1') return '켜짐';
  if (value === 0 || value === '0') return '꺼짐';
  return '확인 불가';
}

function SettingsToggle({ value, disabled, readOnly, onToggle }) {
  const on = Number(value) === 1;
  const label = on ? t('user.modal.notifications.flagOn') : t('user.modal.notifications.flagOff');
  if (readOnly) {
    return (
      <span
        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap ${
          on
            ? 'bg-green-500/20 text-green-200 border border-green-400/40'
            : 'bg-slate-500/20 text-slate-200 border border-slate-400/40'
        }`}
      >
        {label}
      </span>
    );
  }
  return (
    <button
      type="button"
      disabled={disabled}
      title={t('user.modal.account.clickToToggle')}
      onClick={() => onToggle(on ? 0 : 1)}
      className="group inline-flex items-center gap-2 rounded-lg border border-dark-border bg-dark-bg/70 px-2 py-1.5 hover:border-primary/60 hover:bg-primary/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <span
        className={`relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors ${
          on ? 'bg-emerald-500' : 'bg-slate-600'
        }`}
      >
        <span
          className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
            on ? 'translate-x-4' : 'translate-x-0.5'
          }`}
        />
      </span>
      <span className={`text-xs font-semibold ${on ? 'text-emerald-200' : 'text-dark-text-secondary'}`}>
        {label}
      </span>
      <span className="text-[10px] text-dark-text-muted group-hover:text-primary">
        {t('user.modal.account.clickToToggle')}
      </span>
    </button>
  );
}

// The API sends the tier as a slug plus a numeric rank. Labelling off the slug keeps this
// list from drifting when a tier is inserted between two existing ranks, and the rank is
// shown alongside so support can read a plan the web build has no name for yet.
function membershipTierLabel(slug, rank) {
  const key = `user.modal.account.membership.${slug}`;
  const label = t(key);
  if (label === key) {
    return t('user.modal.account.membership.unknown', { rank: Number(rank) || 0 });
  }
  return label;
}

function badgeTierLabel(value) {
  switch (Number(value)) {
    case 1:
      return t('user.modal.account.badge.bronze');
    case 2:
      return t('user.modal.account.badge.silver');
    case 3:
      return t('user.modal.account.badge.gold');
    default:
      return t('user.modal.account.badge.none');
  }
}

function RecordField({ label, children }) {
  return (
    <div className="bg-dark-surface border border-dark-border rounded-lg p-3 min-w-0">
      <p className="text-xs text-dark-text-secondary mb-1">{label}</p>
      <div className="text-sm text-dark-text break-words">{children}</div>
    </div>
  );
}

function getAccountTypeLabel(type) {
  switch (type) {
    case 'OFFICIAL':
      return t('user.accountType.official');
    case 'ADMIN':
      return t('user.accountType.admin');
    case 'BOT':
      return t('user.accountType.bot');
    case 'REVIEWER':
      return t('user.accountType.reviewer');
    case 'TESTER':
      return t('user.accountType.tester');
    case 'PARTNER_CELEB':
      return t('user.accountType.partnerCeleb');
    case 'USER':
      return t('user.accountType.user');
    default:
      return t('user.accountType.user');
  }
}

/**
 * Current user account and activity detail.
 * @param {{ userId: number, token: string, onClose: () => void, onUnauthorized?: () => void, onNavigateUserId?: (id: number) => void, initialUser?: object, runBanAction?: (user: object) => void, isBanPending?: boolean, pendingUserId?: number|null }} props
 */
export default function UserDetailModal({
  userId,
  token,
  onClose,
  onUnauthorized,
  onNavigateUserId,
  runBanAction,
  runSuspensionAction,
  isBanPending = false,
  pendingUserId = null,
}) {
  const { apiEnvironment } = useApiEnvironment();
  const navigate = useNavigate();
  const openModeration = (params) => navigate(`/moderation?${new URLSearchParams({ environment: apiEnvironment, ...params })}`);
  const queryClient = useQueryClient();
  const { scope, authenticated } = useUserDetailScope('user-detail', apiEnvironment, token);
  const canEditAccountSettings = apiEnvironment === API_ENV_TEST;
  const [activeModalTab, setActiveModalTab] = useState('overview');
  const [blocksPage, setBlocksPage] = useState(1);
  const [devicesPage, setDevicesPage] = useState(1);
  const [likesPage, setLikesPage] = useState(1);
  const [receivedLikesPage, setReceivedLikesPage] = useState(1);
  const [accountTypeDraft, setAccountTypeDraft] = useState('USER');
  const [lastUpdatedUserId, setLastUpdatedUserId] = useState(null);

  const detailQuery = useQuery({
    queryKey: [...scope, 'userById', userId],
    queryFn: () => getUserById(userId, token),
    enabled: authenticated && !!userId,
  });

  const blocksQuery = useQuery({
    queryKey: [...scope, 'userBlocks', userId, blocksPage, PAGE_SIZE],
    queryFn: () => getUserBlocks(userId, blocksPage, PAGE_SIZE, token),
    enabled: authenticated && !!userId && activeModalTab === 'blocks',
  });

  const devicesQuery = useQuery({
    queryKey: [...scope, 'userDevices', userId, devicesPage, PAGE_SIZE],
    queryFn: () => getUserDevices(userId, devicesPage, PAGE_SIZE, token),
    enabled: authenticated && !!userId && activeModalTab === 'devices',
  });

  const likesQuery = useQuery({
    queryKey: [...scope, 'userLikes', userId, likesPage, PAGE_SIZE],
    queryFn: () => getUserLikes(userId, likesPage, PAGE_SIZE, token),
    enabled: authenticated && !!userId && activeModalTab === 'likes',
  });

  const receivedLikesQuery = useQuery({
    queryKey: [...scope, 'userLikesReceived', userId, receivedLikesPage, PAGE_SIZE],
    queryFn: () => getUserLikesReceived(userId, receivedLikesPage, PAGE_SIZE, token),
    enabled: authenticated && !!userId && activeModalTab === 'likesReceived',
  });

  const selectedUser = detailQuery.data ?? null;
  const currentAccountType = selectedUser?.account_type ?? selectedUser?.AccountType ?? 'USER';
  const canEditAccountType = isEditableAccountType(currentAccountType);

  const accountQuery = useQuery({
    queryKey: [...scope, 'userAccount', userId],
    queryFn: () => getUserAccount(userId, token),
    enabled: authenticated && !!userId && ['account', 'location'].includes(activeModalTab),
  });

  const notificationsQuery = useQuery({
    queryKey: [...scope, 'userNotifications', userId],
    queryFn: () => getUserNotifications(userId, token),
    enabled: authenticated && !!userId && activeModalTab === 'notifications',
  });

  const settingsMut = useMutation({
    mutationFn: (fields) => {
      if (!canEditAccountSettings) {
        return Promise.reject(new Error(t('user.modal.account.testOnly')));
      }
      return patchUserAccountSettings(userId, fields, token);
    },
    onSuccess: (data) => {
      if (data?.account) {
        queryClient.setQueryData([...scope, 'userAccount', userId], data.account);
      }
      if (data?.notifications) {
        queryClient.setQueryData([...scope, 'userNotifications', userId], data.notifications);
      }
    },
  });

  const accountTypeMut = useMutation({
    mutationFn: (accountType) => patchUserAccountType(userId, accountType, token),
    onMutate: () => setLastUpdatedUserId(null),
    onSuccess: (data) => {
      const nextAccountType = data?.account_type;
      if (!nextAccountType) return;
      queryClient.setQueryData([...scope, 'userById', userId], (current) => ({
        ...(current ?? {}),
        account_type: nextAccountType,
      }));
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['usersSummary'] });
      queryClient.invalidateQueries({ queryKey: ['celebs'] });
      queryClient.invalidateQueries({ queryKey: ['dm-users'] });
      queryClient.invalidateQueries({ queryKey: ['dm-users-summary'] });
      queryClient.invalidateQueries({ queryKey: ['moderation-users'] });
      queryClient.invalidateQueries({ queryKey: ['moderation-user'] });
      setAccountTypeDraft(nextAccountType);
      setLastUpdatedUserId(userId);
    },
  });

  useEffect(() => {
    const err =
      detailQuery.error ||
      blocksQuery.error ||
      devicesQuery.error ||
      likesQuery.error ||
      receivedLikesQuery.error ||
      accountQuery.error ||
      notificationsQuery.error ||
      accountTypeMut.error;
    if (err?.status === 401) {
      onUnauthorized?.();
    }
  }, [
    detailQuery.error,
    blocksQuery.error,
    devicesQuery.error,
    likesQuery.error,
    receivedLikesQuery.error,
    accountQuery.error,
    notificationsQuery.error,
    accountTypeMut.error,
    onUnauthorized,
  ]);

  useEffect(() => {
    setActiveModalTab('overview');
    setBlocksPage(1);
    setDevicesPage(1);
    setLikesPage(1);
    setReceivedLikesPage(1);
  }, [userId, apiEnvironment]);

  useEffect(() => {
    setAccountTypeDraft(currentAccountType);
    setLastUpdatedUserId(null);
  }, [currentAccountType, userId]);

  const blockItems = useMemo(() => blocksQuery.data?.items ?? [], [blocksQuery.data?.items]);
  const blocksTotal = blocksQuery.data?.total_count ?? 0;
  const blocksPageSize = blocksQuery.data?.page_size ?? PAGE_SIZE;
  const blocksCurrentPage = blocksQuery.data?.page ?? blocksPage;
  const blocksTotalPages = Math.max(1, Math.ceil(blocksTotal / blocksPageSize));

  const deviceItems = useMemo(() => devicesQuery.data?.items ?? [], [devicesQuery.data?.items]);
  const devicesTotal = devicesQuery.data?.total_count ?? 0;
  const devicesPageSize = devicesQuery.data?.page_size ?? PAGE_SIZE;
  const devicesCurrentPage = devicesQuery.data?.page ?? devicesPage;
  const devicesTotalPages = Math.max(1, Math.ceil(devicesTotal / devicesPageSize));

  const likeItems = useMemo(() => likesQuery.data?.items ?? [], [likesQuery.data?.items]);
  const likesTotal = likesQuery.data?.total_count ?? 0;
  const likesPageSize = likesQuery.data?.page_size ?? PAGE_SIZE;
  const likesCurrentPage = likesQuery.data?.page ?? likesPage;
  const likesTotalPages = Math.max(1, Math.ceil(likesTotal / likesPageSize));

  const receivedLikeItems = useMemo(() => receivedLikesQuery.data?.items ?? [], [receivedLikesQuery.data?.items]);
  const receivedLikesTotal = receivedLikesQuery.data?.total_count ?? 0;
  const receivedLikesPageSize = receivedLikesQuery.data?.page_size ?? PAGE_SIZE;
  const receivedLikesCurrentPage = receivedLikesQuery.data?.page ?? receivedLikesPage;
  const receivedLikesTotalPages = Math.max(1, Math.ceil(receivedLikesTotal / receivedLikesPageSize));

  const profileSummary = useMemo(() => {
    const u = selectedUser;
    const showLoadingPlaceholder = detailQuery.isLoading && u == null;
    if (showLoadingPlaceholder) {
      return { bioDisplay: '…', numLikesDisplay: '…', pointBalanceDisplay: '…' };
    }
    if (!u) {
      return { bioDisplay: '—', numLikesDisplay: '—', pointBalanceDisplay: '조회 불가' };
    }
    const rawBio = u.bio ?? u.Bio;
    const bioDisplay =
      rawBio != null && String(rawBio).trim() !== '' ? String(rawBio) : '—';
    const n = u.num_likes ?? u.NumLikes;
    const numLikesDisplay = n == null || n === '' ? '0' : String(Number(n));
    const p = u.point_balance ?? u.PointBalance;
    const pointBalanceDisplay =
      u.point_balance_status == null ? '조회 상태 미제공' : u.point_balance_status !== 'available' || p == null || p === '' ? '조회 불가' : Number(p).toLocaleString();
    return { bioDisplay, numLikesDisplay, pointBalanceDisplay };
  }, [selectedUser, detailQuery.isLoading]);

  const handleClose = () => {
    onClose();
    setActiveModalTab('overview');
    setBlocksPage(1);
    setDevicesPage(1);
    setLikesPage(1);
    setReceivedLikesPage(1);
  };

  const handleAccountTypeSave = () => {
    if (!canEditAccountType || accountTypeDraft === currentAccountType) return;
    const confirmed = window.confirm(
      t('user.modal.accountType.confirm', {
        id: userId,
        from: currentAccountType,
        to: accountTypeDraft,
        environment: String(apiEnvironment).toUpperCase(),
      })
    );
    if (confirmed) {
      accountTypeMut.mutate(accountTypeDraft);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 overflow-y-auto">
      <div className="bg-dark-card border border-dark-border rounded-lg shadow-xl max-w-6xl w-full max-h-[min(90vh,900px)] flex flex-col my-auto">
        <div className="flex justify-between items-center px-5 py-4 border-b border-dark-border flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            {selectedUser ? <UserAvatar user={selectedUser} size={40} /> : null}
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-dark-text">{t('user.modal.title', { id: userId })}</h2>
              <p className="text-xs text-dark-text-secondary truncate">
                {selectedUser
                  ? [
                      displayUserEmail(selectedUser),
                      getAccountTypeLabel(selectedUser.account_type ?? selectedUser.AccountType ?? 'USER'),
                    ]
                      .filter(Boolean)
                      .join(' · ')
                  : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {selectedUser && runSuspensionAction && canManageSuspension(selectedUser, apiEnvironment) ? (
              <button type="button" onClick={() => runSuspensionAction(selectedUser)} className="rounded border border-amber-500/50 px-2.5 py-1 text-xs text-amber-400">{t('user.suspension.manage')}</button>
            ) : null}
            {selectedUser && runBanAction ? (
              <UserBanActionButton
                user={selectedUser}
                runBanAction={runBanAction}
                isPending={isBanPending}
                pendingUserId={pendingUserId}
              />
            ) : null}
            <button type="button" className="text-dark-text-secondary hover:text-primary text-sm" onClick={handleClose}>
              {t('user.modal.close')}
            </button>
          </div>
        </div>
        <div className="px-5 py-4 space-y-4 overflow-y-auto min-h-0 flex-1">
          <p className="text-xs font-semibold text-primary">{String(apiEnvironment).toUpperCase()} · 유저 #{userId}</p>
          {(runBanAction || runSuspensionAction) && <p className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-100">Ban과 기간 정지는 수동 계정 조치입니다. 제재 중 새 프로필·Discover 노출은 중단되지만, 기간 정지 해제 후 저장된 태그가 다시 보일 수 있습니다. 태그는 자동 삭제되지 않습니다.</p>}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-dark-surface border border-dark-border rounded-lg p-3 min-w-0 sm:col-span-1">
              <p className="text-xs text-dark-text-secondary mb-1">{t('user.modal.bio')}</p>
              <p className="text-sm text-dark-text whitespace-pre-wrap break-words">{profileSummary.bioDisplay}</p>
            </div>
            <div className="bg-dark-surface border border-dark-border rounded-lg p-3">
              <p className="text-xs text-dark-text-secondary mb-1">{t('user.modal.numLikes')}</p>
              <p className="text-lg font-semibold text-dark-text tabular-nums">{profileSummary.numLikesDisplay}</p>
            </div>
            <div className="bg-dark-surface border border-dark-border rounded-lg p-3">
              <p className="text-xs text-dark-text-secondary mb-1">{t('user.modal.pointBalance')}</p>
              <p className="text-lg font-semibold text-dark-text tabular-nums">{profileSummary.pointBalanceDisplay}</p>
            </div>
          </div>



          <PageTabs variant="segment" label="유저 상세" active={activeModalTab} onChange={setActiveModalTab} items={[
            ['overview', '계정 개요'], ['profileTags', '프로필 태그'], ['stories', 'Story'], ['speech', 'Speech'], ['dm', 'DM'], ['friends', '친구 · 요청'], ['location', '아바타 위치'], ['points', '포인트 원장'], ['account', '설정 · 멤버십'], ['devices', '디바이스'], ['blocks', '차단'], ['notifications', '알림 설정'], ['likes', '보낸 좋아요'], ['likesReceived', '받은 좋아요'], ['moderation', '신고 · 제재 / DM'],
          ].map(([id,label]) => ({id,label}))} />
          {activeModalTab === 'overview' && <div className="grid gap-3 sm:grid-cols-2">
            {detailQuery.error && <p role="alert">계정 개요를 불러오지 못했습니다. <button onClick={() => detailQuery.refetch()}>다시 시도</button></p>}
            <RecordField label="이름">{selectedUser?.name ?? selectedUser?.Name ?? '—'}</RecordField>
            <RecordField label="사용자명">@{selectedUser?.username ?? selectedUser?.Username ?? '—'}</RecordField>
            <RecordField label="유저 ID">{userId}</RecordField>
            <RecordField label="계정 상태">{selectedUser ? getAccountStatusLabel(selectedUser.account_status ?? selectedUser.AccountStatus) : '—'}
              {(selectedUser?.account_status ?? selectedUser?.AccountStatus) === 0 && <p className="mt-1 text-xs text-dark-text-secondary">비활성 사유: {({1:'비활성',2:'탈퇴',3:'Ban',4:'정지'})[getUserAccountInactiveReason(selectedUser)] || '확인 불가'}</p>}
            </RecordField>
            <RecordField label="가입 시각">{formatDateTime(selectedUser?.created_at ?? selectedUser?.CreatedAt)}</RecordField>
            <RecordField label="마지막 로그인">{formatDateTime(selectedUser?.login_at ?? selectedUser?.LoginAt)}</RecordField>
          </div>}
          {activeModalTab === 'profileTags' && <ProfileTagDetail userId={userId} token={token} scope={scope} onUnauthorized={onUnauthorized} />}
          {['stories', 'speech'].includes(activeModalTab) && <UserUGCDetail key={`${apiEnvironment}:${userId}:${activeModalTab}`} userId={userId} token={token} apiEnvironment={apiEnvironment} fixedContentType={activeModalTab === 'stories' ? 'STORY' : 'SPEECH'} onOpenCase={id => openModeration({case_id:id,target_type:'STORY'})} onOpenSpeechCase={id => openModeration({case_id:id,target_type:'SPEECH'})} reportReasons={REPORT_REASONS} />}
          {activeModalTab === 'dm' && <UserDmHistoryPanel userId={userId} token={token} apiEnvironment={apiEnvironment} onUnauthorized={onUnauthorized} onManage={() => openModeration({tab:'DM',target_type:'DM',user_id:userId})} onOpenReport={id => openModeration({tab:'DM',target_type:'DM',case_id:id})} />}
          {activeModalTab === 'location' && <section className="grid gap-3 sm:grid-cols-2" aria-label="위치 관련 계정 설정">
            <RecordField label="Ghost 모드">{accountQuery.isSuccess && !accountQuery.error ? readLocationFlag(accountQuery.data?.ghost_mode) : '확인 불가'}</RecordField>
            <RecordField label="위치 공유">{accountQuery.isSuccess && !accountQuery.error ? readLocationFlag(accountQuery.data?.location_sharing) : '확인 불가'}</RecordField>
          </section>}
          {['friends', 'location', 'points'].includes(activeModalTab) && <UserActivityPanels key={`${apiEnvironment}:${userId}:${activeModalTab}`} tab={activeModalTab} userId={userId} token={token} apiEnvironment={apiEnvironment} onUnauthorized={onUnauthorized} onOpenUser={onNavigateUserId} />}
          {activeModalTab === 'moderation' && <div className="space-y-3 text-sm"><p>신고·계정 Ban·Text Chat 제한과 DM 제재는 각각의 기존 운영 화면에서 확인합니다.</p><button className="rounded border border-primary p-3" onClick={() => openModeration({tab:'Users',user_id:userId})}>신고 · Ban · Text Chat 제한 이력 열기</button><button className="ml-3 rounded border border-primary p-3" onClick={() => openModeration({tab:'DM',target_type:'DM',user_id:userId})}>DM 발신 · 신고 · 제재 이력 열기</button></div>}

          {activeModalTab === 'blocks' ? (
            blocksQuery.isLoading ? (
              <p className="text-dark-text-secondary text-sm">{t('user.table.loading') || 'Loading...'}</p>
            ) : blockItems.length === 0 ? (
              <p className="text-dark-text-secondary text-sm">{t('user.modal.blocks.empty')}</p>
            ) : (
              <div className="space-y-3">
                <div className="overflow-x-auto border border-dark-border rounded-lg">
                  <table className="w-full min-w-[640px]">
                    <thead>
                      <tr className="border-b border-dark-border">
                        <th className="text-left py-2 px-3 text-dark-text-secondary font-medium text-xs">
                          {t('user.modal.blocks.table.userId')}
                        </th>
                        <th className="text-left py-2 px-3 text-dark-text-secondary font-medium text-xs">
                          {t('user.modal.blocks.table.blockId')}
                        </th>
                        <th className="text-left py-2 px-3 text-dark-text-secondary font-medium text-xs">
                          {t('user.modal.blocks.table.username')}
                        </th>
                        <th className="text-left py-2 px-3 text-dark-text-secondary font-medium text-xs">
                          {t('user.modal.blocks.table.name')}
                        </th>
                        <th className="text-left py-2 px-3 text-dark-text-secondary font-medium text-xs">
                          {t('user.modal.blocks.table.createdAt')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {blockItems.map((block, index) => (
                        <tr
                          key={`${block.id ?? block.ID ?? 'block'}-${block.block_id ?? block.BlockID ?? index}`}
                          className="border-b border-dark-border last:border-0"
                        >
                          <td className="py-2 px-3 text-xs text-dark-text-secondary">{block.user_id ?? block.UserID ?? '-'}</td>
                          <td className="py-2 px-3 text-xs text-dark-text-secondary">{block.block_id ?? block.BlockID ?? '-'}</td>
                          <td className="py-2 px-3 text-xs text-dark-text-secondary">
                            {block.blocked_username ?? block.BlockedUsername ?? '-'}
                          </td>
                          <td className="py-2 px-3 text-xs text-dark-text-secondary">{block.blocked_name ?? block.BlockedName ?? '-'}</td>
                          <td className="py-2 px-3 text-xs text-dark-text-secondary">
                            {formatDateTime(block.created_at ?? block.CreatedAt)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center justify-between text-xs text-dark-text-secondary">
                  <span>{t('user.pagination.page', { page: blocksCurrentPage, totalPages: blocksTotalPages })}</span>
                  <div className="space-x-2">
                    <button
                      type="button"
                      className="px-2 py-1 rounded border border-dark-border disabled:opacity-40 hover:border-primary hover:text-primary transition-colors"
                      onClick={() => setBlocksPage((prev) => Math.max(1, prev - 1))}
                      disabled={blocksCurrentPage <= 1}
                    >
                      {t('user.pagination.prev')}
                    </button>
                    <button
                      type="button"
                      className="px-2 py-1 rounded border border-dark-border disabled:opacity-40 hover:border-primary hover:text-primary transition-colors"
                      onClick={() => setBlocksPage((prev) => Math.min(blocksTotalPages, prev + 1))}
                      disabled={blocksCurrentPage >= blocksTotalPages}
                    >
                      {t('user.pagination.next')}
                    </button>
                  </div>
                </div>
              </div>
            )
          ) : null}

          {activeModalTab === 'devices' ? (
            devicesQuery.isLoading ? (
              <p className="text-dark-text-secondary text-sm">{t('user.table.loading') || 'Loading...'}</p>
            ) : deviceItems.length === 0 ? (
              <p className="text-dark-text-secondary text-sm">{t('user.modal.devices.empty')}</p>
            ) : (
              <div className="space-y-3">
                <div className="overflow-x-auto border border-dark-border rounded-lg">
                  <table className="w-full min-w-[900px]">
                    <thead>
                      <tr className="border-b border-dark-border">
                        <th className="text-left py-2 px-3 text-dark-text-secondary font-medium text-xs">
                          {t('user.modal.devices.table.id')}
                        </th>
                        <th className="text-left py-2 px-3 text-dark-text-secondary font-medium text-xs">
                          {t('user.modal.devices.table.deviceId')}
                        </th>
                        <th className="text-left py-2 px-3 text-dark-text-secondary font-medium text-xs">
                          {t('user.modal.devices.table.appPlatform')}
                        </th>
                        <th className="text-left py-2 px-3 text-dark-text-secondary font-medium text-xs">
                          {t('user.modal.devices.table.appVersion')}
                        </th>
                        <th className="text-left py-2 px-3 text-dark-text-secondary font-medium text-xs">
                          {t('user.modal.devices.table.isActive')}
                        </th>
                        <th className="text-left py-2 px-3 text-dark-text-secondary font-medium text-xs">
                          {t('user.modal.devices.table.hasToken')}
                        </th>
                        <th className="text-left py-2 px-3 text-dark-text-secondary font-medium text-xs">
                          {t('user.modal.devices.table.createdAt')}
                        </th>
                        <th className="text-left py-2 px-3 text-dark-text-secondary font-medium text-xs">
                          {t('user.modal.devices.table.updatedAt')}
                        </th>
                        <th className="text-left py-2 px-3 text-dark-text-secondary font-medium text-xs">
                          {t('user.modal.devices.table.deletedAt')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {deviceItems.map((device, index) => {
                        const hasToken = device.has_token ?? device.HasToken ?? false;
                        const isActive = (device.is_active ?? device.IsActive) === 1;
                        const rawPlatform = device.app_platform ?? device.AppPlatform;
                        const platform = normalizePlatform(rawPlatform);
                        const rawAppVersion = device.app_version ?? device.AppVersion;
                        return (
                          <tr
                            key={`${device.id ?? device.ID ?? 'device'}-${device.device_id ?? device.DeviceID ?? index}`}
                            className="border-b border-dark-border last:border-0"
                          >
                            <td className="py-2 px-3 text-xs text-dark-text-secondary">{device.id ?? device.ID ?? '-'}</td>
                            <td className="py-2 px-3 text-xs text-dark-text-secondary font-mono">
                              {device.device_id ?? device.DeviceID ?? '-'}
                            </td>
                            <td className="py-2 px-3 text-xs text-dark-text-secondary">
                              {platform ? (
                                <PlatformBadge platform={platform} />
                              ) : rawPlatform != null && String(rawPlatform).trim() !== '' ? (
                                String(rawPlatform)
                              ) : (
                                '-'
                              )}
                            </td>
                            <td className="py-2 px-3 text-xs text-dark-text-secondary font-mono">
                              {rawAppVersion != null && String(rawAppVersion).trim() !== ''
                                ? String(rawAppVersion)
                                : '-'}
                            </td>
                            <td className="py-2 px-3">
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                  isActive ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'
                                }`}
                              >
                                {isActive ? t('user.modal.devices.active') : t('user.modal.devices.inactive')}
                              </span>
                            </td>
                            <td className="py-2 px-3">
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${
                                  hasToken
                                    ? 'bg-green-500/10 text-green-400 border-green-500/20'
                                    : 'bg-red-500/15 text-red-400 border-red-500/30'
                                }`}
                              >
                                {hasToken ? t('user.modal.devices.hasToken.yes') : t('user.modal.devices.hasToken.no')}
                              </span>
                            </td>
                            <td className="py-2 px-3 text-xs text-dark-text-secondary">
                              {formatDateTime(device.created_at ?? device.CreatedAt)}
                            </td>
                            <td className="py-2 px-3 text-xs text-dark-text-secondary">
                              {formatDateTime(device.updated_at ?? device.UpdatedAt)}
                            </td>
                            <td className="py-2 px-3 text-xs text-dark-text-secondary">
                              {formatDateTime(device.deleted_at ?? device.DeletedAt)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center justify-between text-xs text-dark-text-secondary">
                  <span>{t('user.pagination.page', { page: devicesCurrentPage, totalPages: devicesTotalPages })}</span>
                  <div className="space-x-2">
                    <button
                      type="button"
                      className="px-2 py-1 rounded border border-dark-border disabled:opacity-40 hover:border-primary hover:text-primary transition-colors"
                      onClick={() => setDevicesPage((prev) => Math.max(1, prev - 1))}
                      disabled={devicesCurrentPage <= 1}
                    >
                      {t('user.pagination.prev')}
                    </button>
                    <button
                      type="button"
                      className="px-2 py-1 rounded border border-dark-border disabled:opacity-40 hover:border-primary hover:text-primary transition-colors"
                      onClick={() => setDevicesPage((prev) => Math.min(devicesTotalPages, prev + 1))}
                      disabled={devicesCurrentPage >= devicesTotalPages}
                    >
                      {t('user.pagination.next')}
                    </button>
                  </div>
                </div>
              </div>
            )
          ) : null}

          {activeModalTab === 'account' && selectedUser ? (
            <section className="rounded-lg border border-dark-border bg-dark-surface p-3">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <p className="text-xs text-dark-text-secondary">{t('user.modal.accountType.title')}</p>
                  <p className="mt-1 text-sm text-dark-text">
                    {t('user.modal.accountType.environment', { environment: String(apiEnvironment).toUpperCase() })}
                  </p>
                  <p className="mt-1 text-xs text-dark-text-muted">{t('user.modal.accountType.scopeHint')}</p>
                </div>
                {canEditAccountType ? (
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <label className="sr-only" htmlFor={`account-type-${userId}`}>
                      {t('user.modal.accountType.title')}
                    </label>
                    <select
                      id={`account-type-${userId}`}
                      value={accountTypeDraft}
                      disabled={accountTypeMut.isPending}
                      onChange={(event) => {
                        setAccountTypeDraft(event.target.value);
                        setLastUpdatedUserId(null);
                      }}
                      className="rounded-md border border-dark-border bg-dark-bg px-3 py-2 text-sm text-dark-text disabled:opacity-50"
                    >
                      {EDITABLE_ACCOUNT_TYPES.map((accountType) => (
                        <option key={accountType} value={accountType}>
                          {getAccountTypeLabel(accountType)} ({accountType})
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      disabled={accountTypeMut.isPending || accountTypeDraft === currentAccountType}
                      onClick={handleAccountTypeSave}
                      className="rounded-md border border-primary px-3 py-2 text-sm font-medium text-primary hover:bg-primary/10 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      {accountTypeMut.isPending
                        ? t('user.modal.accountType.saving')
                        : t('user.modal.accountType.save')}
                    </button>
                  </div>
                ) : (
                  <p className="text-xs text-amber-300">{t('user.modal.accountType.separateFlow')}</p>
                )}
              </div>
              {accountTypeMut.isError ? (
                <p className="mt-2 text-xs text-red-400">{t('user.modal.accountType.saveError')}</p>
              ) : null}
              {accountTypeMut.isSuccess && lastUpdatedUserId === userId ? (
                <p className="mt-2 text-xs text-green-400">{t('user.modal.accountType.saveSuccess')}</p>
              ) : null}
            </section>
          ) : null}

          {activeModalTab === 'account' ? (
            accountQuery.isLoading ? (
              <p className="text-dark-text-secondary text-sm">{t('user.table.loading') || 'Loading...'}</p>
            ) : accountQuery.data == null ? (
              <p className="text-dark-text-secondary text-sm">{t('user.modal.account.empty')}</p>
            ) : (
              <div className="space-y-3">
                {!canEditAccountSettings ? (
                  <p className="text-xs text-dark-text-muted">{t('user.modal.account.testOnly')}</p>
                ) : (
                  <p className="text-xs text-dark-text-secondary">{t('user.modal.account.editHint')}</p>
                )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <RecordField label={t('user.modal.account.fields.gameMode')}>
                  <SettingsToggle
                    value={accountQuery.data.game_mode}
                    readOnly={!canEditAccountSettings}
                    disabled={settingsMut.isPending}
                    onToggle={(v) => settingsMut.mutate({ game_mode: v })}
                  />
                </RecordField>
                <RecordField label={t('user.modal.account.fields.ghostMode')}>
                  <SettingsToggle
                    value={accountQuery.data.ghost_mode}
                    readOnly={!canEditAccountSettings}
                    disabled={settingsMut.isPending}
                    onToggle={(v) => settingsMut.mutate({ ghost_mode: v })}
                  />
                </RecordField>
                <RecordField label={t('user.modal.account.fields.locationSharing')}>
                  <SettingsToggle
                    value={accountQuery.data.location_sharing}
                    readOnly={!canEditAccountSettings}
                    disabled={settingsMut.isPending}
                    onToggle={(v) => settingsMut.mutate({ location_sharing: v })}
                  />
                </RecordField>
                <RecordField label={t('user.modal.account.fields.membershipTier')}>
                  <div>
                    {membershipTierLabel(
                      accountQuery.data.membership_tier,
                      accountQuery.data.membership_tier_rank,
                    )}
                  </div>
                  {accountQuery.data.membership_expires_at ? (
                    <p className="mt-1 text-xs text-dark-text-secondary">
                      {t('user.modal.account.fields.membershipExpiresAt', {
                        at: formatDateTime(accountQuery.data.membership_expires_at),
                      })}
                    </p>
                  ) : null}
                  <p className="mt-1 text-xs text-dark-text-muted">
                    {t('user.modal.account.fields.membershipTierHint')}
                  </p>
                </RecordField>
                <RecordField label={t('user.modal.account.fields.rpsBadgeTier')}>
                  <div>{badgeTierLabel(accountQuery.data.rps_badge_tier)}</div>
                  {Array.isArray(accountQuery.data.rps_season_badges) && accountQuery.data.rps_season_badges.length > 0 ? (
                    <ul className="mt-2 space-y-1 text-xs text-dark-text-secondary">
                      {accountQuery.data.rps_season_badges.map((badge) => (
                        <li key={badge.season_key}>
                          {badge.season_key}: {badgeTierLabel(badge.tier)} (#{badge.rank}, {badge.wins})
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </RecordField>
                <RecordField label={t('user.modal.account.fields.id')}>
                  {accountQuery.data.id ?? accountQuery.data.ID ?? '-'}
                </RecordField>
                <RecordField label={t('user.modal.account.fields.userId')}>
                  {accountQuery.data.user_id ?? accountQuery.data.UserID ?? '-'}
                </RecordField>
                <RecordField label={t('user.modal.account.fields.createdAt')}>
                  {formatDateTimeKST(accountQuery.data.created_at ?? accountQuery.data.CreatedAt)}
                </RecordField>
                <RecordField label={t('user.modal.account.fields.updatedAt')}>
                  {formatDateTimeKST(accountQuery.data.updated_at ?? accountQuery.data.UpdatedAt)}
                </RecordField>
                <RecordField label={t('user.modal.account.fields.deletedAt')}>
                  {formatDateTimeKST(accountQuery.data.deleted_at ?? accountQuery.data.DeletedAt)}
                </RecordField>
              </div>
              {settingsMut.isError && activeModalTab === 'account' ? (
                <p className="text-sm text-red-400">
                  {settingsMut.error?.message || t('user.modal.account.saveError')}
                </p>
              ) : null}
              </div>
            )
          ) : null}

          {activeModalTab === 'notifications' ? (
            notificationsQuery.isLoading ? (
              <p className="text-dark-text-secondary text-sm">{t('user.table.loading') || 'Loading...'}</p>
            ) : notificationsQuery.data == null ? (
              <p className="text-dark-text-secondary text-sm">{t('user.modal.notifications.empty')}</p>
            ) : (
              <div className="space-y-3">
                {!canEditAccountSettings ? (
                  <p className="text-xs text-dark-text-muted">{t('user.modal.account.testOnly')}</p>
                ) : (
                  <p className="text-xs text-dark-text-secondary">{t('user.modal.account.editHint')}</p>
                )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <RecordField label={t('user.modal.notifications.fields.id')}>
                  {notificationsQuery.data.id ?? notificationsQuery.data.ID ?? '-'}
                </RecordField>
                <RecordField label={t('user.modal.notifications.fields.userId')}>
                  {notificationsQuery.data.user_id ?? notificationsQuery.data.UserID ?? '-'}
                </RecordField>
                <RecordField label={t('user.modal.notifications.fields.likeRequest')}>
                  <SettingsToggle
                    value={notificationsQuery.data.like_request ?? notificationsQuery.data.LikeRequest}
                    readOnly={!canEditAccountSettings}
                    disabled={settingsMut.isPending}
                    onToggle={(v) => settingsMut.mutate({ like_request: v })}
                  />
                </RecordField>
                <RecordField label={t('user.modal.notifications.fields.productUpdate')}>
                  <SettingsToggle
                    value={notificationsQuery.data.product_update ?? notificationsQuery.data.ProductUpdate}
                    readOnly={!canEditAccountSettings}
                    disabled={settingsMut.isPending}
                    onToggle={(v) => settingsMut.mutate({ product_update: v })}
                  />
                </RecordField>
                <RecordField label={t('user.modal.notifications.fields.createdAt')}>
                  {formatDateTimeKST(notificationsQuery.data.created_at ?? notificationsQuery.data.CreatedAt)}
                </RecordField>
                <RecordField label={t('user.modal.notifications.fields.updatedAt')}>
                  {formatDateTimeKST(notificationsQuery.data.updated_at ?? notificationsQuery.data.UpdatedAt)}
                </RecordField>
                <RecordField label={t('user.modal.notifications.fields.deletedAt')}>
                  {formatDateTimeKST(notificationsQuery.data.deleted_at ?? notificationsQuery.data.DeletedAt)}
                </RecordField>
              </div>
              {settingsMut.isError && activeModalTab === 'notifications' ? (
                <p className="text-sm text-red-400">
                  {settingsMut.error?.message || t('user.modal.account.saveError')}
                </p>
              ) : null}
              </div>
            )
          ) : null}

          {activeModalTab === 'likes' ? (
            likesQuery.isLoading ? (
              <p className="text-dark-text-secondary text-sm">{t('user.table.loading') || 'Loading...'}</p>
            ) : likeItems.length === 0 ? (
              <p className="text-dark-text-secondary text-sm">{t('user.modal.likes.empty')}</p>
            ) : (
              <div className="space-y-3">
                <div className="overflow-x-auto border border-dark-border rounded-lg">
                  <table className="w-full min-w-[520px]">
                    <thead>
                      <tr className="border-b border-dark-border">
                        <th className="text-left py-2 px-3 text-dark-text-secondary font-medium text-xs">
                          {t('user.modal.likes.table.recipientId')}
                        </th>
                        <th className="text-left py-2 px-3 text-dark-text-secondary font-medium text-xs">
                          {t('user.modal.likes.table.username')}
                        </th>
                        <th className="text-left py-2 px-3 text-dark-text-secondary font-medium text-xs">
                          {t('user.modal.likes.table.name')}
                        </th>
                        <th className="text-left py-2 px-3 text-dark-text-secondary font-medium text-xs">
                          {t('user.modal.likes.table.createdAt')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {likeItems.map((like, index) => (
                        <tr
                          key={`${like.id ?? like.ID ?? 'like'}-${like.recipient_id ?? like.RecipientID ?? index}`}
                          className="border-b border-dark-border last:border-0"
                        >
                          <td className="py-2 px-3 text-xs text-dark-text-secondary">
                            {like.recipient_id ?? like.RecipientID ?? '-'}
                          </td>
                          <td className="py-2 px-3 text-xs text-dark-text-secondary">
                            {like.recipient_username ?? like.RecipientUsername ?? '-'}
                          </td>
                          <td className="py-2 px-3 text-xs text-dark-text-secondary">
                            {like.recipient_name ?? like.RecipientName ?? '-'}
                          </td>
                          <td className="py-2 px-3 text-xs text-dark-text-secondary">
                            {formatDateTime(like.created_at ?? like.CreatedAt)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center justify-between text-xs text-dark-text-secondary">
                  <span>{t('user.pagination.page', { page: likesCurrentPage, totalPages: likesTotalPages })}</span>
                  <div className="space-x-2">
                    <button
                      type="button"
                      className="px-2 py-1 rounded border border-dark-border disabled:opacity-40 hover:border-primary hover:text-primary transition-colors"
                      onClick={() => setLikesPage((prev) => Math.max(1, prev - 1))}
                      disabled={likesCurrentPage <= 1}
                    >
                      {t('user.pagination.prev')}
                    </button>
                    <button
                      type="button"
                      className="px-2 py-1 rounded border border-dark-border disabled:opacity-40 hover:border-primary hover:text-primary transition-colors"
                      onClick={() => setLikesPage((prev) => Math.min(likesTotalPages, prev + 1))}
                      disabled={likesCurrentPage >= likesTotalPages}
                    >
                      {t('user.pagination.next')}
                    </button>
                  </div>
                </div>
              </div>
            )
          ) : null}

          {activeModalTab === 'likesReceived' ? (
            receivedLikesQuery.isLoading ? (
              <p className="text-dark-text-secondary text-sm">{t('user.table.loading') || 'Loading...'}</p>
            ) : receivedLikeItems.length === 0 ? (
              <p className="text-dark-text-secondary text-sm">{t('user.modal.likesReceived.empty')}</p>
            ) : (
              <div className="space-y-3">
                <div className="overflow-x-auto border border-dark-border rounded-lg">
                  <table className="w-full min-w-[520px]">
                    <thead>
                      <tr className="border-b border-dark-border">
                        <th className="text-left py-2 px-3 text-dark-text-secondary font-medium text-xs">
                          {t('user.modal.likesReceived.table.senderId')}
                        </th>
                        <th className="text-left py-2 px-3 text-dark-text-secondary font-medium text-xs">
                          {t('user.modal.likesReceived.table.username')}
                        </th>
                        <th className="text-left py-2 px-3 text-dark-text-secondary font-medium text-xs">
                          {t('user.modal.likesReceived.table.name')}
                        </th>
                        <th className="text-left py-2 px-3 text-dark-text-secondary font-medium text-xs">
                          {t('user.modal.likesReceived.table.createdAt')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {receivedLikeItems.map((row, index) => (
                        <tr
                          key={`${row.id ?? row.ID ?? 'recv'}-${row.user_id ?? row.UserID ?? index}`}
                          className="border-b border-dark-border last:border-0"
                        >
                          <td className="py-2 px-3 text-xs text-dark-text-secondary">{row.user_id ?? row.UserID ?? '-'}</td>
                          <td className="py-2 px-3 text-xs text-dark-text-secondary">
                            {row.sender_username ?? row.SenderUsername ?? '-'}
                          </td>
                          <td className="py-2 px-3 text-xs text-dark-text-secondary">
                            {row.sender_name ?? row.SenderName ?? '-'}
                          </td>
                          <td className="py-2 px-3 text-xs text-dark-text-secondary">
                            {formatDateTime(row.created_at ?? row.CreatedAt)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center justify-between text-xs text-dark-text-secondary">
                  <span>
                    {t('user.pagination.page', { page: receivedLikesCurrentPage, totalPages: receivedLikesTotalPages })}
                  </span>
                  <div className="space-x-2">
                    <button
                      type="button"
                      className="px-2 py-1 rounded border border-dark-border disabled:opacity-40 hover:border-primary hover:text-primary transition-colors"
                      onClick={() => setReceivedLikesPage((prev) => Math.max(1, prev - 1))}
                      disabled={receivedLikesCurrentPage <= 1}
                    >
                      {t('user.pagination.prev')}
                    </button>
                    <button
                      type="button"
                      className="px-2 py-1 rounded border border-dark-border disabled:opacity-40 hover:border-primary hover:text-primary transition-colors"
                      onClick={() =>
                        setReceivedLikesPage((prev) => Math.min(receivedLikesTotalPages, prev + 1))
                      }
                      disabled={receivedLikesCurrentPage >= receivedLikesTotalPages}
                    >
                      {t('user.pagination.next')}
                    </button>
                  </div>
                </div>
              </div>
            )
          ) : null}


        </div>
        <div className="px-5 py-3 border-t border-dark-border flex justify-end flex-shrink-0">
          <button
            type="button"
            className="px-4 py-2 rounded-md bg-primary text-black text-sm font-medium hover:bg-highlight transition-colors"
            onClick={handleClose}
          >
            {t('user.modal.close')}
          </button>
        </div>
      </div>
    </div>
  );
}
